/**
 * Instrument-zu-Sample-Zuordnung und zentraler Sample-Loader.
 *
 * Die Sample-URLs werden über `new URL(..., import.meta.url)` aufgelöst. Damit
 * bündelt Vite die lokal ausgelieferten WAV-Dateien in den Produktions-Build
 * (relative Pfade, keine externen Laufzeit-URLs, kein CDN).
 *
 * Der Loader ist von der konkreten Web-Audio-Dekodierung entkoppelt: Er
 * arbeitet gegen ein schmales {@link SampleDecoder}-Interface und ist so ohne
 * Browser unit-testbar.
 */

import { DrumInstrument, INSTRUMENT_ORDER } from '../model/types';

/** Statische URLs der ausgelieferten One-Shot-Samples. */
export const SAMPLE_URLS: Readonly<Record<DrumInstrument, string>> = {
  [DrumInstrument.Crash]: new URL('./samples/crash.wav', import.meta.url).href,
  [DrumInstrument.Ride]: new URL('./samples/ride.wav', import.meta.url).href,
  [DrumInstrument.HiHat]: new URL('./samples/hihat.wav', import.meta.url).href,
  [DrumInstrument.HighTom]: new URL('./samples/hightom.wav', import.meta.url)
    .href,
  [DrumInstrument.MidTom]: new URL('./samples/midtom.wav', import.meta.url)
    .href,
  [DrumInstrument.Snare]: new URL('./samples/snare.wav', import.meta.url).href,
  [DrumInstrument.FloorTom]: new URL('./samples/floortom.wav', import.meta.url)
    .href,
  [DrumInstrument.Bassdrum]: new URL('./samples/bassdrum.wav', import.meta.url)
    .href,
};

/**
 * Schmale Abstraktion über das Laden und Dekodieren eines Samples. In der
 * Anwendung wird dies vom Web-Audio-Adapter erfüllt; in Tests durch eine
 * Attrappe.
 */
export interface SampleDecoder {
  fetch(url: string): Promise<ArrayBuffer>;
  decode(data: ArrayBuffer): Promise<AudioBuffer>;
}

/** Ergebnis eines Ladevorgangs. */
export interface SampleLoadResult {
  /** Erfolgreich geladene und dekodierte Buffer je Instrument. */
  readonly buffers: ReadonlyMap<DrumInstrument, AudioBuffer>;
  /** Instrumente, deren Sample nicht geladen werden konnte. */
  readonly failed: readonly DrumInstrument[];
}

/**
 * Lädt und dekodiert die Instrument-Samples genau einmal und hält die
 * fertigen {@link AudioBuffer} zur Wiederverwendung vor.
 *
 * Fehlerstrategie: Schlägt ein einzelnes Sample fehl, bleibt **nur dieses
 * Instrument stumm**; die übrige Wiedergabe läuft weiter. Begründung: Die
 * Samples werden mit dem Build ausgeliefert, ein Fehlschlag ist also die
 * seltene Ausnahme. Ein partiell nutzbares Schlagzeug ist für den
 * Probeneinsatz deutlich robuster als eine komplett blockierte Wiedergabe.
 */
export class SampleLoader {
  readonly #decoder: SampleDecoder;
  readonly #urls: Readonly<Record<DrumInstrument, string>>;
  readonly #buffers = new Map<DrumInstrument, AudioBuffer>();
  #inFlight: Promise<SampleLoadResult> | null = null;
  #result: SampleLoadResult | null = null;

  constructor(
    decoder: SampleDecoder,
    urls: Readonly<Record<DrumInstrument, string>> = SAMPLE_URLS,
  ) {
    this.#decoder = decoder;
    this.#urls = urls;
  }

  /** Ob bereits ein abgeschlossener Ladevorgang vorliegt. */
  get isLoaded(): boolean {
    return this.#result !== null;
  }

  /**
   * Lädt alle Samples. Mehrfache Aufrufe teilen sich denselben Ladevorgang
   * bzw. liefern das bereits vorhandene Ergebnis – jedes Sample wird also nur
   * einmal geladen und dekodiert.
   */
  load(): Promise<SampleLoadResult> {
    if (this.#result) {
      return Promise.resolve(this.#result);
    }
    if (this.#inFlight) {
      return this.#inFlight;
    }
    this.#inFlight = this.#loadAll();
    return this.#inFlight;
  }

  async #loadAll(): Promise<SampleLoadResult> {
    const failed: DrumInstrument[] = [];

    await Promise.all(
      INSTRUMENT_ORDER.map(async (instrument) => {
        if (this.#buffers.has(instrument)) {
          return;
        }
        try {
          const data = await this.#decoder.fetch(this.#urls[instrument]);
          const buffer = await this.#decoder.decode(data);
          this.#buffers.set(instrument, buffer);
        } catch (error) {
          failed.push(instrument);
          console.error(
            `Sample konnte nicht geladen werden: ${instrument}`,
            error,
          );
        }
      }),
    );

    const result: SampleLoadResult = {
      buffers: new Map(this.#buffers),
      failed,
    };
    this.#result = result;
    this.#inFlight = null;
    return result;
  }
}
